/* =========================================================================
   format.ts — formatters for currency, percent, multiples, ratios.
   ========================================================================= */

export function compactCurrency(
  v: number | null | undefined,
  opts?: { symbol?: string },
): string {
  if (v == null || !Number.isFinite(v)) return "—";
  const n = v === 0 ? 0 : v; // normalise -0
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(v);
  const prefix = opts?.symbol || "$";
  if (abs >= 1e9) return `${sign}${prefix}${(abs / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${sign}${prefix}${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}${prefix}${(abs / 1e3).toFixed(1)}K`;
  return `${sign}${prefix}${abs.toFixed(0)}`;
}

export function fullCurrency(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  const n = v === 0 ? 0 : v;
  const sign = n < 0 ? "-" : "";
  return `${sign}$${Math.round(Math.abs(n)).toLocaleString("en-AU")}`;
}

/** Cent-preserving currency for FAIL-severity evidence: checks that trip on sub-dollar
 *  residuals must not print their trigger as "$0" (loop#003). Grouped, always two decimals. */
export function preciseCurrency(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  const n = v === 0 ? 0 : v; // normalise -0
  const sign = n < 0 ? "-" : "";
  return `${sign}$${Math.abs(n).toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function pct(v: number | null | undefined, digits?: number): string {
  if (v == null || !Number.isFinite(v)) return "—";
  const n = v === 0 ? 0 : v;
  const d = digits == null ? 1 : digits;
  return `${(n * 100).toFixed(d)}%`;
}

export function num(v: number | null | undefined, digits?: number): string {
  if (v == null || !Number.isFinite(v)) return "—";
  const n = v === 0 ? 0 : v;
  const d = digits == null ? 0 : digits;
  return n.toLocaleString("en-AU", {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });
}

/** Equity multiples, DSCR, and other unitless ratio displays (always uses ×). */
export function multiple(v: number | null | undefined, digits = 2): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return `${num(v, digits)}×`;
}

export function delta(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  const n = v === 0 ? 0 : v;
  const sign = n > 0 ? "+" : n < 0 ? "-" : "";
  return `${sign}${pct(Math.abs(n), 1)}`;
}

export function pctSigned(v: number | null | undefined, digits = 1): string {
  if (v == null || !Number.isFinite(v)) return "—";
  const n = v === 0 ? 0 : v;
  const sign = n >= 0 ? "+" : "";
  return `${sign}${pct(n, digits)}`;
}

export function years(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  const n = v === 0 ? 0 : v;
  return `${n} ${n === 1 ? "year" : "years"}`;
}

export function payback(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v) || v <= 0) return "Beyond horizon";
  return `Y${v}`;
}

export function sqm(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  const n = v === 0 ? 0 : v;
  return `${Math.round(n).toLocaleString("en-AU")} sqm`;
}

/** Self-explaining reason shown alongside a "n/a" operator IRR (operator never recovers capex). */
export const OPERATOR_IRR_NA_REASON =
  "no IRR — operator doesn't recover capex in this scenario";

/**
 * Operator IRR display. When the operator never recovers its capex within the horizon (e.g. the
 * Downside: every-year-negative cashflow → no real root), reddamIrr is null and a bare "—" reads as
 * a calc error. Render an explicit "n/a" instead (pair with OPERATOR_IRR_NA_REASON at the call site).
 * When an IRR exists this is just Fmt.pct(v, digits) — unchanged.
 */
export function operatorIrr(v: number | null | undefined, digits = 1): string {
  if (v == null || !Number.isFinite(v)) return "n/a";
  return pct(v, digits);
}

export const Fmt = {
  compactCurrency,
  fullCurrency,
  preciseCurrency,
  pct,
  pctSigned,
  num,
  delta,
  years,
  sqm,
  payback,
  operatorIrr,
  multiple,
};
